const getCurrentCart = async () => {
  await fetch("/cart.js")
    .then((res) => res.json())
    .then((cart) => {
      window.cart = cart;
    });

  window.setGlobalCart();
};

/**
 * Returns true if customer has tag.
 *
 * @param {string} tag - A tag in a customer's Shopify profile.
 */
const customerHasTag = (tag) => {
  if (window.ShopifyAnalytics.meta.page.customerId) {
    const customerTags = document.getElementById("header").dataset.customertags;
    return customerTags !== null && customerTags.includes(tag);
  }
  return false;
};

/**
 * Checks customer's tags and cart to determine if a discounted price should be shown on pdp.
 *
 * For swatches: If customer is tagged swatch-buyer OR has >= the maxSwatchesForDiscount in cart they'll see full pricing
 * otherwise they'll see the discounted price.
 *
 * For swatch books: If customer is tagged swatch-book-buyer OR has >= the maxSwatchBooksForDiscount in cart they'll see full pricing
 * otherwise they'll see the discounted price.
 *
 * See product.js for setSwatchPricePDP usage
 * @param {*} price - The initial price of the item passed in.
 * @param {HtmlElement} priceContainer - The element where the price is being modified.
 * @param {string} productType - The product type: "swatch", "swatch-book"
 */
// eslint-disable-next-line no-unused-vars
async function setSwatchPricePDP(price, priceContainer, productType) {
  if (!window.cart) await getCurrentCart();
  const { cart } = window;
  const formatPrice = (priceToFormat) =>
    `$${(priceToFormat / 100).toFixed(priceToFormat % 100 > 0 ? 2 : 0)}`;

  const setDiscountPrice = (element, originalPrice, salePrice = null) => {
    const newPriceElement = element.cloneNode(true);
    const salePriceHTML = salePrice
      ? `<span class="text-brand-darkerMocha" aria-label="Sale price ${salePrice}"> ${salePrice}</span>`
      : "";
    newPriceElement.innerHTML = `
      <span class="text-brand-darkerMocha line-through decoration-2" aria-label="Original price ${originalPrice}">
        <span class="text-brand-deepMocha-75">${originalPrice}</span>
      </span>${salePriceHTML}`;
    element.replaceWith(newPriceElement);
  };

  const setFullPrice = (element, priceToFormat) => {
    const newPriceElement = element.cloneNode(true);
    newPriceElement.innerHTML = formatPrice(priceToFormat);
    element.replaceWith(newPriceElement);
  };

  const handleSwatchDiscount = () => {
    const { maxSwatchesForDiscount } = window;
    const swatchItemsInCart = cart.items.reduce(
      (count, item) =>
        item.product_type === "Swatch" ? count + item.quantity : count,
      0,
    );
    const swatchDiscountLabel = document.getElementById(
      "swatch-discount-label",
    );
    const tagsWrapper = document.getElementById("product-label-tags");
    const showDiscount =
      swatchItemsInCart < maxSwatchesForDiscount &&
      !customerHasTag("swatch-buyer");
    if (showDiscount) {
      setDiscountPrice(priceContainer, formatPrice(price), "$0");
      if (!swatchDiscountLabel) {
        tagsWrapper.insertAdjacentHTML(
          "afterend",
          `<div id="swatch-discount-label" class="text-darkSeaGlass uppercase mb-[32px] text-base">
            Your first ${maxSwatchesForDiscount} swatches are free
          </div>`,
        );
      }
    } else {
      swatchDiscountLabel?.remove();
      setFullPrice(priceContainer, price);
    }
  };

  const handleSwatchBookDiscount = () => {
    const { maxSwatchBooksForDiscount } = window;
    const swatchBooksInCart = cart.items.reduce(
      (count, item) =>
        item.product_type === "Swatch Book" ? count + item.quantity : count,
      0,
    );
    const showDiscount =
      !customerHasTag("swatch-book-buyer") &&
      swatchBooksInCart < maxSwatchBooksForDiscount;
    if (showDiscount) {
      setDiscountPrice(priceContainer, formatPrice(price), "$0");
    } else {
      setFullPrice(priceContainer, price);
    }
  };

  if (productType === "swatch") {
    handleSwatchDiscount();
  } else if (productType === "swatch-book") {
    handleSwatchBookDiscount();
  }
}

// See collection.js for setSwatchPricePLP usage
// eslint-disable-next-line no-unused-vars
function setSwatchPricePLP(product, cart) {
  if (product.type === "Swatch" || product.type === "Swatch Book") {
    const { maxSwatchesForDiscount, maxSwatchBooksForDiscount } = window;
    const productType = product.type;
    const quantityToDiscount =
      productType === "Swatch"
        ? maxSwatchesForDiscount
        : maxSwatchBooksForDiscount;
    const customerTagToCheck =
      productType === "Swatch" ? "swatch-buyer" : "swatch-book-buyer";
    let showDiscountPrice = true;

    const swatchItemsInCart = cart.items.reduce(
      (count, item) =>
        item.product_type === productType ? count + item.quantity : count,
      0,
    );

    if (swatchItemsInCart >= quantityToDiscount) {
      showDiscountPrice = false;
    }

    if (customerHasTag(customerTagToCheck)) showDiscountPrice = false;

    if (showDiscountPrice) {
      return `<div aria-label="Original price ${product.price}" class="font-bold collection-price"><div class="text-brand-darkerMocha line-through decoration-2"><span class="text-brand-deepMocha-75">${product.price}</span></div>
<div class="text-brand-darkerMocha" aria-label="Sale price $0">$0</div></div>`;
    }
    return `<div class="font-bold">${product.price}</div>`;
  }
  return null;
}

function setSwatchPriceInCarousels(swatchesList, productType) {
  const { cart } = window;
  const { maxSwatchesForDiscount, maxSwatchBooksForDiscount } = window;
  const quantityToDiscount =
    productType === "Swatch"
      ? maxSwatchesForDiscount
      : maxSwatchBooksForDiscount;
  const customerTagToCheck =
    productType === "Swatch" ? "swatch-buyer" : "swatch-book-buyer";
  let showDiscountPrice = true;

  const swatchItemsInCart = cart.items.reduce(
    (count, item) =>
      item.product_type === productType ? count + item.quantity : count,
    0,
  );

  if (swatchItemsInCart >= quantityToDiscount) {
    showDiscountPrice = false;
  }

  if (customerHasTag(customerTagToCheck)) showDiscountPrice = false;

  // For updating product carousel title dynamically in product--utm-swatch-carousel
  const utmSwatchCarouselTitle = document.querySelector(
    "utm-swatch-carousel .main-title",
  );
  if (utmSwatchCarouselTitle) {
    utmSwatchCarouselTitle.textContent = showDiscountPrice
      ? "Free Swatches"
      : "Swatches";
  }

  swatchesList.forEach((product) => {
    const price = product.querySelector("[data-original-price]").dataset
      .originalPrice;
    const priceWrapper = product.querySelector(".carousel-item-price");

    if (showDiscountPrice) {
      priceWrapper.innerHTML = `
        <div aria-label="Original price ${price}" class="text-brand-darkerMocha line-through decoration-2">
          <span class="text-brand-deepMocha-75">${price}</span>
        </div>
        <div class="text-brand-darkerMocha" aria-label="Sale price $0">$0</div>`;
    } else {
      priceWrapper.innerHTML = `${price}`;
    }
  });
}
const swatchesList = document.querySelectorAll("[data-swatch]");
const swatchBooksList = document.querySelectorAll("[data-swatch-book]");

if (swatchesList.length) {
  document.addEventListener("cartChange", () => {
    setSwatchPriceInCarousels(swatchesList, "Swatch");
  });
}
if (swatchBooksList.length) {
  document.addEventListener("cartChange", () => {
    setSwatchPriceInCarousels(swatchBooksList, "Swatch Book");
  });
}
